import produce, { setAutoFreeze } from "immer";
import {
  getAvailableVariablesForStep,
  getAvailableGlobalVariables,
} from "libs/types/src/lib/getAvailableVariables";
import { nanoid } from "nanoid";
import {
  ButtonOption,
  createEmbedMessageStepId,
  createIfConditionStepId,
  createPlaceholderStep,
  createPlaceholderStepId,
  createQuestionMessageStepId,
  isPlaceholderStep,
  isPlaceHolderStepId,
  isWhenUserResponse,
  StepConfigMultipleSelect,
  StepConfigSendSurvey,
  TriggerForEditor,
  TriggerStepCreate,
  TriggerTransitionCreate,
  WhenButtonUserResponse,
} from "@dots-projects/types";
import { ThreadAutoArchiveDuration } from "discord-api-types/v10";
import invariant from "ts-invariant";
import _ from "lodash";

type DynamicTriggerSteps = (
  inputStep?: TriggerStepCreate
) => TriggerStepCreate[];

export type StepTemplate = {
  title: string;
  description: string;
  steps: DynamicTriggerSteps;
  internal?: boolean;
};

const STEP_ID_REGEX = /%step(\w+)/;

const generateStepId = () => {
  return nanoid(6);
};

export const copySteps = (
  trigger: TriggerForEditor,
  stepToCopy: TriggerStepCreate
) => {
  const step = trigger.steps.find((s) => s.id === stepToCopy.id)!;

  const copyStepTemplates: {
    [taskType: string]: StepTemplate;
  } = {
    send_multiple_select: {
      title: `Send survey (multiple selections) copy step`,
      description: "Send a survey with multiple selections",
      steps: () => {
        invariant(step.type === "send_multiple_select");
        invariant(step.configs);
        invariant(step.configs.$$editorPosition);
        const copyMultipleSelectStep: TriggerStepCreate = {
          id: `%step0`,
          type: "send_multiple_select",
          configs: {
            ...(step.configs as StepConfigMultipleSelect),
            options: step.configs.options.map((option) => ({ ...option })),
            $$editorPosition: {
              x: step.configs!.$$editorPosition.x + 100,
              y: step.configs!.$$editorPosition.y,
            },
            $$metadata: {
              copied: true,
            },
          },
          transitions: [
            {
              targetStepId: createIfConditionStepId(`%step0`),
              when: {
                userResponse: {
                  type: "multiple-select",
                  userId: "{{user.id}}",
                  messageId: `{{%step0.messageId}}`,
                },
              },
            },
          ],
        };

        invariant(copyMultipleSelectStep.type === "send_multiple_select");

        const options = copyMultipleSelectStep.configs.options;
        options.forEach((option) => {
          option.id = `${generateStepId()}-%step0`;
        });

        const steps = options
          .map<TriggerStepCreate[]>((option, index) => [
            {
              id: createIfConditionStepId(`%step${index}`),
              name: `ifElse${index}`,
              type: "idle",
              configs: {
                $$metadata: {
                  copied: true,
                },
              },
              if: {
                condition: {
                  "%step0.userResponse.optionIds": {
                    array_contains: option.id,
                  },
                },
                elseStepId:
                  index < options.length - 1
                    ? createIfConditionStepId(`%step${index + 1}`)
                    : createPlaceholderStepId("%stepDone"),
              },
              transitions: [
                { targetStepId: createPlaceholderStepId(`%step${index}Yes`) },
              ],
            },
            {
              id: createPlaceholderStepId(`%step${index}Yes`),
              name: `ifElseYes${index}`,
              type: "idle",
              configs: {
                $$stepCopied: true,
              },
              transitions: [
                {
                  targetStepId:
                    index < options.length - 1
                      ? createIfConditionStepId(`%step${index + 1}`)
                      : createPlaceholderStepId("%stepDone"),
                },
              ],
            },
          ])
          .flat();
        return [copyMultipleSelectStep, ...steps];
      },
    },
    send_survey: {
      title: `Send a survey (single selection)`,
      description: "Copy a step",
      steps: () => {
        invariant(step.type === "send_survey");
        invariant(step.configs);
        invariant(step.configs.$$editorPosition);

        const surveyConfig = step.configs as StepConfigSendSurvey;
        const options = (step.configs.options as ButtonOption[]).map(
          (option) => ({ ...option })
        );

        return [
          {
            id: `%step0`,
            type: "send_survey",
            configs: {
              ...surveyConfig,
              options,
              $$editorPosition: {
                x: step.configs!.$$editorPosition.x + 100,
                y: step.configs!.$$editorPosition.y,
              },
              $$metadata: {
                copied: true,
              },
            },
            transitions: options.map((o, idx) => ({
              targetStepId: createPlaceholderStepId(`%step${idx}-${o.label}`),
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: o.id,
                  messageId: `{{%step0.messageId}}`,
                },
              },
            })),
          },
        ] as TriggerStepCreate[];
      },
    },
    conditional_steps: {
      title: "Steps with conditional transitions",
      description: "Copy steps with conditional transitions",
      steps: () => {
        invariant(
          ["send_message", "test_captcha", "open_modal"].includes(step.type)
        );

        const conditionalTransition = step
          .transitions[0] as TriggerTransitionCreate;

        const conditionalStep = trigger.steps.find(
          (s) => s.id === conditionalTransition.targetStepId
        )!;

        const conditionalTransitions =
          conditionalStep.transitions as TriggerTransitionCreate[];

        const placeHolderChildrenSteps = conditionalTransitions.filter((t) => {
          return isPlaceHolderStepId(t.targetStepId);
        });

        const nonPlaceHolderChildrenTransitions = conditionalTransitions
          .filter((t) => {
            return !isPlaceHolderStepId(t.targetStepId);
          })
          .map((t, index) => ({
            ...t,
            oldId: t.targetStepId,
            targetStepId: `%step${index}nonPlaceholder`,
          }));

        const emailNoPlaceHolderChildrenSteps =
          nonPlaceHolderChildrenTransitions.map((t) => {
            const step = trigger.steps.find((s) => s.id === t.oldId)!;
            return produce(step, (draftStep) => {
              draftStep.id = t.targetStepId!;
              const conditionalStepTransition = (
                draftStep.transitions as TriggerTransitionCreate[]
              ).find((t) => (t.targetStepId = conditionalStep.id));
              if (conditionalStepTransition) {
                draftStep.transitions = [
                  {
                    targetStepId: `%step1`,
                    when: conditionalTransition.when,
                  } as TriggerTransitionCreate,
                ];
              } else {
                draftStep.transitions = [];
              }
            });
          });

        const steps = [
          {
            id: `%step0`,
            type: step.type,
            name: step.name || "Copy",
            configs: {
              ...step.configs,
            },
            transitions: [
              {
                targetStepId: `%step1`,
              },
            ],
          },
          {
            id: "%step1",
            type: "idle",
            name: conditionalStep.name,
            transitions: [
              //Placeholder transitions
              ...placeHolderChildrenSteps.map((t, index) => ({
                ...t,
                targetStepId: createPlaceholderStepId(
                  `%step${index}placeholder`
                ),
              })),
              //Non placeholder transitions
              ...nonPlaceHolderChildrenTransitions.map((t) => ({
                when: t.when,
                targetStepId: t.targetStepId,
              })),
            ],
          },
          ...emailNoPlaceHolderChildrenSteps,
        ] as TriggerStepCreate[];

        return steps;
      },
    },
    question: {
      title: "Ask for email copy",
      description: "Copy ask for email steps",
      steps: () => {
        invariant(step.type === "send_message");

        const copyTransitions: TriggerTransitionCreate[] = [];
        step.transitions.forEach((_t, index) => {
          copyTransitions.push({
            targetStepId: createPlaceholderStepId(`%step${index + 1}`),
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "$channelId",
              },
            },
          });
        });

        const steps = [
          {
            id: `%step0`,
            type: "send_message",
            name: step.name || "Send a message",
            configs: {
              ...step.configs,
            },
            transitions: copyTransitions,
          },
        ] as TriggerStepCreate[];

        return steps;
      },
    },
    modal: {
      title: "Open modal copy",
      description: "Copy open modal steps",
      steps: () => {
        const conditionalTransition = step
          .transitions[0] as TriggerTransitionCreate;

        return [
          {
            id: `%step0`,
            type: step.type,
            name: step.name || "Copy",
            configs: {
              ...step.configs,
            },
            transitions: [
              {
                targetStepId: createPlaceholderStepId("%step1"),
                when: {
                  userResponse: {
                    ...conditionalTransition!.when!.userResponse,
                    modalId: `{{%step0.modalId}}`,
                  },
                },
              },
            ],
          },
        ] as TriggerStepCreate[];
      },
    },
    default: {
      title: "Copy default step",
      description: "For steps that doesn't depend on other steps",
      steps: () => {
        return [
          {
            id: `%step0`,
            type: step.type,
            name: step.name || "Copy",
            configs: {
              ...step.configs,
            },
            transitions: [],
          },
        ] as TriggerStepCreate[];
      },
    },
  };

  const templateKey: string = step.configs?.$$metadata?.groupType || step.type;

  const newSteps = createStepsFromTemplate(
    copyStepTemplates[templateKey]
      ? copyStepTemplates[templateKey]
      : copyStepTemplates["default"],
    trigger
  );

  let originalSteps: TriggerStepCreate[] = [];

  if (templateKey === "send_multiple_select") {
    const multipleSelectSteps: any[] = [];
    const allMultipleSelectSteps = () => {
      //Conditional step
      let ifStep: TriggerStepCreate | undefined;
      let childIfStep: TriggerStepCreate;
      ifStep = trigger.steps.find(
        (s) => s.id === step.transitions[0].targetStepId
      )!;
      childIfStep = trigger.steps.find(
        (s) => s.id === ifStep?.transitions[0].targetStepId
      )!;
      do {
        multipleSelectSteps.push(ifStep);
        multipleSelectSteps.push(childIfStep);
        ifStep = trigger.steps.find((s) => s.id === ifStep?.if?.elseStepId);
        if (ifStep?.id.includes("Done")) {
          multipleSelectSteps.push(ifStep);
          return [
            produce(step, (draftStep) => draftStep),
            ...multipleSelectSteps,
          ] as TriggerStepCreate[];
        }
      } while (ifStep);
      return;
    };
    originalSteps = allMultipleSelectSteps()!;
  } else if (templateKey === "send_survey") {
    const transitionsIds = step.transitions.map((t) => t.targetStepId);

    // Get all the steps that are part of the survey
    const surveySteps = trigger.steps.filter((s) => {
      return transitionsIds.includes(s.id);
    });
    originalSteps = [step, ...surveySteps];
  } else if (templateKey === "conditional_steps") {
    invariant(["test_captcha", "send_message"].includes(step.type));
    const conditionalTransition = step.transitions[0];
    const conditionalStep = trigger.steps.find(
      (s) => s.id === conditionalTransition.targetStepId
    )!;

    const conditionalStepTransitions = conditionalStep.transitions;
    const childrenSteps = trigger.steps.filter((s) => {
      return conditionalStepTransitions
        .map((t) => t.targetStepId)
        .includes(s.id);
    });

    originalSteps = [step, conditionalStep, ...childrenSteps];
  } else if (templateKey === "question") {
    const questionTransitionsIds = step.transitions.map((t) => t.targetStepId);
    const questionSteps = trigger.steps.filter((s) =>
      questionTransitionsIds.includes(s.id)
    )!;
    originalSteps = [step, ...questionSteps];
  } else if (templateKey === "modal") {
    const conditionalTransition = step.transitions[0];
    const conditionalStep = trigger.steps.find(
      (s) => s.id === conditionalTransition.targetStepId
    )!;

    originalSteps = [step, conditionalStep];
  } else {
    originalSteps = [step];
  }

  return setPositionToCopySteps(newSteps, originalSteps, 250);
};

const setPositionToCopySteps = (
  newSteps: TriggerStepCreate[],
  originalSteps: TriggerStepCreate[],
  xOffset: number
) => {
  //Order node steps by x position in asc mode
  const orderByXPosition = (a: TriggerStepCreate, b: TriggerStepCreate) => {
    const aXPosition =
      a.configs!.$$editorPosition?.x ?? a.configs!.$$editorPositionWhenNode?.x!;
    const bXPosition =
      b.configs!.$$editorPosition?.x ?? b.configs!.$$editorPositionWhenNode?.x!;
    return aXPosition - bXPosition;
  };

  const sortOriginalSteps = [...originalSteps].sort(orderByXPosition);
  const xPositions = sortOriginalSteps.map((s, index) => ({
    x: s.configs!.$$editorPosition?.x ?? s.configs!.$$editorPositionWhenNode?.x,
    nextX:
      sortOriginalSteps[index + 1]?.configs!.$$editorPosition?.x ??
      sortOriginalSteps[index + 1]?.configs!.$$editorPositionWhenNode?.x,
    stepId: s.id,
  }));
  const rightmostNodePosition = Math.max(
    ...xPositions.map((position) => position.x!)
  );

  const copyStepsWithPosition = produce(newSteps, (draftSteps) => {
    draftSteps.forEach((step, index) => {
      const originalStep = originalSteps[index];
      const position =
        originalStep.configs!.$$editorPosition! ||
        originalStep.configs!.$$editorPositionWhenNode!;
      // idle steps don't have configs
      if (!step.configs) {
        step.configs = {};
      }

      const xPosition = xPositions.find(
        (xPosition) => xPosition.stepId === originalStep.id
      )!;

      const diffBeetweenXPosition =
        Math.abs(xPosition.x!) -
        (xPosition.nextX ? Math.abs(xPosition.nextX) : 50);

      step.configs!.$$editorPosition = {
        x: xOffset + rightmostNodePosition + Math.abs(diffBeetweenXPosition),
        y: position.y,
      };
    });
  });
  return copyStepsWithPosition;
};

/**
 * Templates used in AddItemForm
 *
 * - We can inlcude multiple steps in a template.
 * - Step ids such as `%step0` will be automatically generated.
 * - `$channelId` will be replaced with an available ref in the scope, e.g. `{{stepXYZ.channelId}}`
 */
export const STEP_TEMPLATES: StepTemplate[] = [
  {
    title: "Create channel & Send message",
    description: "Create a channel and Send a chat message",
    steps: () => [
      {
        type: "create_channel",
        name: "Create a channel",
        id: "%step0",
        configs: {
          channelName: "onboard-{{user.username}}",
          channelType: "onboarding",
          isPrivate: true,
          userIds: ["{{user.id}}"],
        },
        transitions: [{ targetStepId: "%step1" }],
      },
      {
        type: "send_message",
        name: "Send a message",
        id: `%step1`,
        configs: {
          channelId: "{{%step0.channelId}}",
          removeUrlEmbeds: false,
          message: "Hello! What's your first name?",
        },
        transitions: [
          {
            targetStepId: createPlaceholderStepId("%step2"),
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "{{%step0.channelId}}",
              },
            },
          },
        ],
      },
    ],
  },
  {
    title: "Send message",
    description: "Send a chat message",
    steps: () => [
      {
        type: "send_message",
        name: "Send a message",
        id: `%step0`,
        configs: {
          removeUrlEmbeds: false,
          channelId: "$channelId",
          message: "Hello world!",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Ask a question",
    description: "Send a chat message and wait for the user's answer",
    steps: () => [
      {
        type: "send_message",
        name: "Ask a question",
        id: createQuestionMessageStepId("%step0"),
        configs: {
          removeUrlEmbeds: false,
          channelId: "$channelId",
          message: "Do you like cookies?",
          $$metadata: {
            groupType: "question",
          },
        },
        transitions: [
          {
            targetStepId: createPlaceholderStepId("%step1"),
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "$channelId",
              },
            },
          },
        ],
      },
    ],
  },
  {
    title: "Send survey (single selection)",
    description: "Send a survey",
    steps: (inputStep) => {
      let options: ButtonOption[] = [
        { emoji: "🟡", id: `africa-%step0`, label: "Africa" },
        { emoji: "🟢", id: `na-%step0`, label: "North America" },
      ];
      if (
        inputStep?.type === "send_survey" &&
        inputStep.configs.type === "buttons"
      ) {
        options = inputStep.configs.options;
      }
      return [
        {
          type: "send_survey",
          name: "Send a survey (single selection)",
          id: `%step0`,
          configs: {
            channelId: "$channelId",
            message: "Where are you from?",
            type: "buttons",
            options,
          },
          transitions: options.map((o, idx) => ({
            targetStepId: createPlaceholderStepId(`%step${idx}-${o.label}`),
            when: {
              userResponse: {
                type: "button",
                userId: "{{user.id}}",
                buttonId: o.id,
                messageId: `{{%step0.messageId}}`,
              },
            },
          })),
        },
      ];
    },
  },
  {
    title: "Send survey (multiple selections)",
    description: "Send a survey",
    steps: (inputStep) => {
      const multiSelectStep: TriggerStepCreate = inputStep ?? {
        type: "send_multiple_select",
        name: "Send a survey (multiple selections)",
        id: `%step0`,
        configs: {
          removeUrlEmbeds: false,
          channelId: "$channelId",
          message: "Where are you from?",
          type: "buttons",
          options: [
            { emoji: "🟡", id: `africa-%step0`, label: "Africa" },
            { emoji: "🔵", id: `asia-%step0`, label: "Asia" },
            { emoji: "🟢", id: `na-%step0`, label: "North America" },
          ],
        },
        transitions: [
          {
            targetStepId: createIfConditionStepId(`%step0`),
            when: {
              userResponse: {
                type: "multiple-select",
                userId: "{{user.id}}",
                messageId: `{{%step0.messageId}}`,
              },
            },
          },
        ],
      };
      invariant(multiSelectStep.type === "send_multiple_select");
      const options = multiSelectStep.configs.options;
      const steps = options
        .map<TriggerStepCreate[]>((option, index) => [
          {
            id: createIfConditionStepId(`%step${index}`),
            name: `ifElse${index}`,
            type: "idle",
            if: {
              condition: {
                "%step0.userResponse.optionIds": {
                  array_contains: option.id,
                },
              },
              elseStepId:
                index < options.length - 1
                  ? createIfConditionStepId(`%step${index + 1}`)
                  : createPlaceholderStepId("%stepDone"),
            },
            transitions: [
              { targetStepId: createPlaceholderStepId(`%step${index}Yes`) },
            ],
          },
          {
            id: createPlaceholderStepId(`%step${index}Yes`),
            name: `ifElseYes${index}`,
            type: "idle",
            transitions: [
              {
                targetStepId:
                  index < options.length - 1
                    ? createIfConditionStepId(`%step${index + 1}`)
                    : createPlaceholderStepId("%stepDone"),
              },
            ],
          },
        ])
        .flat();
      return [multiSelectStep, ...steps];
    },
  },
  {
    title: "Ask for email",
    description:
      "Ask the user for their email address who has option to skip it",
    // options: {
    //   questionType: 'email',
    //   skippable: true
    // },
    steps: () => [
      {
        type: "send_message",
        name: "Send a message",
        id: `%step0`,
        configs: {
          removeUrlEmbeds: false,
          channelId: "$channelId",
          message: "Hello! What's your email address?",
          $$metadata: {
            groupType: "conditional_steps",
          },
        },
        transitions: [
          {
            targetStepId: `%step1`,
          },
        ],
      },
      {
        id: "%step1",
        type: "idle",
        name: "Wait for email",
        transitions: [
          {
            targetStepId: "%step2",
            name: "Wrong email format",
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "$channelId",
                and: [
                  { content: { hasnot: "email" } },
                  { content: { neq: "skip" } },
                ],
              },
            },
          },
          {
            targetStepId: createPlaceholderStepId("%step3"),
            name: "Success",
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "$channelId",
                content: { has: "email" },
              },
            },
          },
          {
            targetStepId: createPlaceholderStepId("%step4"),
            name: "Skip",
            when: {
              userResponse: {
                type: "message",
                userId: "{{user.id}}",
                channelId: "$channelId",
                content: "skip",
              },
            },
          },
        ],
      },
      {
        type: "send_message",
        name: "Not an email",
        id: `%step2`,
        configs: {
          removeUrlEmbeds: false,
          channelId: "$channelId",
          message:
            "This doesn't look like a valid email address. Please try again",
        },
        transitions: [
          {
            targetStepId: `%step1`,
          },
        ],
      },
    ],
  },
  {
    title: "Send embed message",
    description:
      "Send a chat message with colored border, embedded images, text fields, and other fancy properties.",
    steps: () => [
      {
        type: "send_message",
        name: "Send a embed message",
        id: createEmbedMessageStepId(`%step0`),
        configs: {
          channelId: "$channelId",
          message: JSON.stringify({
            content: "Hello world!",
            embeds: [
              {
                title: "This is an embed message :wave:",
                color: 4321431,
                timestamp: "2022-10-17T17:45:05.892Z",
                url: "https://discord.com",
                author: {
                  name: "Author name",
                  url: "https://discord.com",
                  icon_url: "https://cdn.discordapp.com/embed/avatars/0.png",
                },
                thumbnail: {
                  url: "https://cdn.discordapp.com/embed/avatars/0.png",
                },
              },
            ],
          }),
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Delete channel",
    description: "Deletes a channel created during flow.",
    steps: () => [
      {
        type: "delete_channel",
        name: "Delete a channel",
        id: "%step0",
        configs: {
          delaySeconds: 60 * 5,
          channelId: "$channelId",
        },
        isGoal: true,
        transitions: [],
      },
    ],
  },
  {
    title: "Send Captcha",
    description: "Send a captcha",
    steps: () => [
      {
        type: "test_captcha",
        name: "Send a captcha",
        id: `%step0`,
        configs: {
          channelId: `$channelId`,
          userId: "{{user.id}}",
          message:
            "Hello <@{{user.id}}> you aren't a robot right? Please solve this",
          $$succeedEditorHtmlMessage: `<p>Hello <span class="mention" data-index="3" data-denotation-char="{{" data-id="<@user.id>" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> you aren't a robot right? Please solve this.</p>`,
          failMessage:
            "Sorry <@{{user.id}}> sorry you failed all attempts. Please request a new captcha!",
          $$failEditorHtmlMessage: `<p>Sorry <span class="mention" data-index="0" data-id="<@user.id>" data-denotation-char="{{" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> sorry you failed all attempts. Please request a new captcha!</p`,
          ephemeral: false,
          $$metadata: {
            groupType: "conditional_steps",
          },
        },
        transitions: [{ targetStepId: `%step1` }],
      },
      {
        id: "%step1",
        type: "idle",
        name: "Wait for captcha response",
        transitions: [
          {
            targetStepId: createPlaceholderStepId("%step2"),
            name: "Captcha passes",
            when: {
              userResponse: {
                type: "captcha-result",
                channelId: `$channelId`,
                userId: "{{user.id}}",
                succeeded: true,
              },
            },
          },
          {
            targetStepId: createPlaceholderStepId("%step3"),
            name: "Captcha fails",
            when: {
              userResponse: {
                type: "captcha-result",
                channelId: `$channelId`,
                userId: "{{user.id}}",
                succeeded: false,
              },
            },
          },
        ],
      },
    ],
  },
  {
    title: "Send Captcha (visible only to the user)",
    description:
      "For flows where there isn’t a new channel being created, we recommend using this step.",
    steps: () => [
      {
        type: "test_captcha",
        name: "Send a captcha (visible only to the user)",
        id: `%step0`,
        configs: {
          channelId: "{{interaction.channelId}}",
          userId: "{{user.id}}",
          message:
            "Hello <@{{user.id}}> you aren't a robot right? Please solve this",
          $$succeedEditorHtmlMessage: `<p>Hello <span class="mention" data-index="3" data-denotation-char="{{" data-id="<@user.id>" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> you aren't a robot right? Please solve this.</p>`,
          failMessage:
            "Sorry <@{{user.id}}> sorry you failed all attempts. Please request a new captcha!",
          $$failEditorHtmlMessage: `<p>Sorry <span class="mention" data-index="0" data-id="<@user.id>" data-denotation-char="{{" data-value="@username}}"><span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span></span> sorry you failed all attempts. Please request a new captcha!</p`,
          ephemeral: true,
          $$metadata: {
            groupType: "conditional_steps",
          },
        },
        transitions: [{ targetStepId: `%step1` }],
      },
      {
        id: "%step1",
        type: "idle",
        name: "Wait for captcha response",
        transitions: [
          {
            targetStepId: createPlaceholderStepId("%step2"),
            name: "Captcha passes",
            when: {
              userResponse: {
                type: "captcha-result",
                channelId: "{{interaction.channelId}}",
                userId: "{{user.id}}",
                succeeded: true,
              },
            },
          },
          {
            targetStepId: createPlaceholderStepId("%step3"),
            name: "Captcha fails",
            when: {
              userResponse: {
                type: "captcha-result",
                channelId: "{{interaction.channelId}}",
                userId: "{{user.id}}",
                succeeded: false,
              },
            },
          },
        ],
      },
    ],
  },
  {
    title: "Edit Roles",
    description: "Add or remove the roles of users",
    steps: () => [
      {
        id: "%step0",
        type: "edit_roles",
        name: "Edit roles",
        configs: {
          userIds: [],
          addRoles: [],
          removeRoles: [],
          ephemeral: false,
          acknowledge: true,
          followUpMessage: "Great, the role has been applied!",
          followUpChannelId: "$channelId",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Edit Dots Tags",
    description: "Add or remove Dots tags of users",
    steps: () => [
      {
        id: "%step0",
        type: "edit_dots_tags",
        name: "Edit Dots tags",
        configs: {
          userIds: [],
          addTags: [],
          removeTags: [],
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Create thread",
    description: "Create a thread in a channel",
    steps: () => [
      {
        id: "%step0",
        type: "create_thread",
        name: "Create a thread",
        configs: {
          type: "public",
          channelId: "",
          archiveDurationMinutes: ThreadAutoArchiveDuration.OneWeek, // 1 week
          title: "My first thread!",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Show Modal",
    description: "Show modal",
    steps: () => [
      {
        type: "open_modal",
        name: "Show a modal",
        id: `%step0`,
        configs: {
          title: "Modal title",
          fields: [
            {
              id: "field", // is this needed?
              type: "open_modal", // is this needed?
              label: "Modal label",
              style: "short",
            },
          ],
          $$metadata: {
            groupType: "modal",
          },
        },
        transitions: [
          {
            targetStepId: createPlaceholderStepId("%step2"),
            when: {
              userResponse: {
                type: "modal-submit",
                userId: "{{user.id}}",
                modalId: `{{%step0.modalId}}`,
              },
            },
          },
        ],
      },
    ],
  },
  {
    title: "Push data to Zapier",
    description:
      "Zapier step is available on the upgraded plan. Please upgrade and ping us!",
    steps: () => [
      {
        id: "%step0",
        type: "send_webhook",
        configs: {
          appId: "$appId",
          eventType: "automation.email_capture",
          payload: {},
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Internal delete channel",
    description:
      "Template to be used internally when a users checks delete a channel property from a create_channel step",
    internal: true,
    steps: () => [
      {
        type: "delete_channel",
        name: "Delete a channel",
        id: "$$step-%step0",
        configs: {
          delaySeconds: 10 * 60, //10 minutes by default
          channelId: "$channelId",
        },
        isGoal: false,
        transitions: [],
      },
    ],
  },
  {
    title: "Kick member",
    description: "Kick member",
    steps: () => [
      {
        id: "%step0",
        name: "Kick member",
        type: "kick_member",
        configs: {
          reason: "",
          userId: "{{user.id}}",
          delaySeconds: 0,
          testMode: true,
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Change nickname",
    description: "Change flow's user nickname.",
    steps: () => [
      {
        id: "%step0",
        type: "change_nickname",
        configs: {
          nickname: "",
          userId: "$userId",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Save user field",
    description: "Store a custom field for a user",
    steps: () => [
      {
        id: "%step0",
        type: "save_user_field",
        name: "Save user field",
        configs: {
          userId: "",
          field: "email",
          value: "",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "Find user",
    description: "Find a user by a custom field",
    steps: () => [
      {
        id: "%step0",
        type: "find_user",
        name: "Find a user",
        configs: {
          field: "email",
          value: "",
        },
        transitions: [],
      },
    ],
  },
  {
    title: "API Step (Make a HTTP request)",
    description: "Get data from an API endpoint and uses it in the flow",
    steps: () => [
      {
        id: "%step0",
        type: "send_http_request",
        name: "API Step",
        configs: {
          url: "",
          method: "POST",
          body: "",
          headers: {},
        },
        transitions: [],
      },
    ],
  },
];

function replaceStepIds(
  idMap: { [oldId: string]: string },
  steps: TriggerStepCreate[],
  shouldReplaceId: boolean
) {
  // Note this seems like a sloppy implementation that might cause issues for some special cases.
  const replaceIdInObject = (input: any) => {
    if (typeof input !== "object" || input === null) return input;
    const objAsString = JSON.stringify(input);
    let newString = objAsString;

    for (const id of Object.keys(idMap)) {
      newString = newString.replaceAll(id, () => idMap[id]);
    }
    return JSON.parse(newString);
  };

  const newSteps: TriggerStepCreate[] = steps.map((s) => ({
    ...s,
    configs: replaceIdInObject(s.configs),
    id: shouldReplaceId ? idMap[s.id] ?? s.id : s.id,
    if: replaceIdInObject(s.if),
    transitions: s.transitions.map((t) => {
      const targetStepId = idMap[t.targetStepId] ?? t.targetStepId;
      const when = replaceIdInObject(t.when);
      return {
        ...t,
        targetStepId,
        when,
      };
    }),
  }));
  return newSteps;
}

/**
 * Generate unique step ids and replace all the references
 *
 * @param steps
 * @returns steps with generated ids
 */
export function generateStepIds(steps: TriggerStepCreate[]) {
  const stepsWithTempId = steps
    .map((step) => ({ match: step.id.match(STEP_ID_REGEX), step }))
    .filter((o) => o.match);
  const idMap = stepsWithTempId.reduce((map, { match, step }) => {
    invariant(match);
    map[step.id] = step.id.replace(
      STEP_ID_REGEX,
      `${step.type}-${match[1]}-${generateStepId()}`
    );
    return map;
  }, {} as { [oldId: string]: string });

  const newSteps = replaceStepIds(idMap, steps, true);
  return newSteps;
}

/**
 *
 * @param steps
 * @returns placeholder steps that display as "+" in the editor
 */
function createPlaceholderSteps(steps: TriggerStepCreate[]) {
  const phStepIds = new Set<string>();
  const existingPhStepIds = steps.reduce(
    (set, step) => (isPlaceholderStep(step) ? set.add(step.id) : set),
    new Set<string>()
  );
  steps.forEach((s) =>
    s.transitions.forEach((t) => {
      if (
        isPlaceHolderStepId(t.targetStepId) &&
        !existingPhStepIds.has(t.targetStepId)
      ) {
        phStepIds.add(t.targetStepId);
      }
    })
  );
  return Array.from(phStepIds).map<TriggerStepCreate>((id) => ({
    type: "idle",
    id,
    transitions: [],
  }));
}

/**
 * Replace variable references based on what's available in the trigger. Right now it only includes a naiive solution that replace `$channelId` with the actual ref such as `{{step0.channelId}}
 *
 * This function looks at both global varialbes and all prior steps.
 *
 * Example 1: if the trigger type is `message_create`, we'll get `{{channelId}}` which is available as global variable.
 *
 * Example 2: If there is an existing `create_channel` step in the trigger, we'll get `{{step-create_channel-AG3AG.channelId}}
 *
 * @param steps
 * @param trigger
 * @param parentStepId
 * @returns updated steps if any
 */
export function autoFillVariables(
  steps: TriggerStepCreate[],
  trigger: TriggerForEditor,
  parentStepId?: string
) {
  // TODO naiive approach that only fills channelId, we need a generalized solution
  // TODO should remove the use of any
  const allSteps = steps.concat(trigger.steps);
  const isParentStepIdValid = allSteps.some((s) => s.id === parentStepId);
  const vars =
    parentStepId && isParentStepIdValid
      ? getAvailableVariablesForStep(trigger, parentStepId)
      : getAvailableGlobalVariables(trigger);
  let channelIdRef = vars["channelId"] ? "channelId" : null;
  Object.keys(vars).forEach((stepId) => {
    const stepVars: any = vars[stepId];
    if (stepVars && stepVars.channelId) {
      channelIdRef = `${stepId}.channelId`;
    }
  });
  if (channelIdRef) {
    const channelIdInTemplate = `{{${channelIdRef}}}`;
    return produce(steps, (draft) => {
      draft.forEach((step: any) => {
        if (step.configs?.channelId === "$channelId") {
          step.configs.channelId = channelIdInTemplate;
        }
        step.transitions.forEach((t: any) => {
          if (isWhenUserResponse(t.when)) {
            if (t.when.userResponse.channelId === "$channelId") {
              t.when.userResponse.channelId = channelIdInTemplate;
            }
          }
        });
      });
    });
  } else {
    return steps;
  }
}

/**
 * In order to get questions and answers from executions when there is an idle step right after a send_message step
 * we need to add in the idle step a reference to the send_message step
 */
function configureIdleStepAfterSendMessage(steps: TriggerStepCreate[]) {
  return produce(steps, (draftSteps) => {
    const idStepMap = draftSteps.reduce(
      (map, step) => map.set(step.id, step),
      new Map<string, TriggerStepCreate>()
    );
    const idleReferences: { idleStepId: string; referenceStepId: string }[] =
      [];
    draftSteps.forEach((step) => {
      // If the current step is a send_message step and has a transition to an idle step we save the reference
      // in order to reference the send_message step in the idle step
      if (step.type === "send_message") {
        step.transitions
          .filter((t) => {
            const targetStep = idStepMap.get(t.targetStepId);
            return targetStep?.type === "idle";
          })
          .forEach((t) => {
            idleReferences.push({
              referenceStepId: step.id,
              idleStepId: t.targetStepId,
            });
          });
      }
    });

    draftSteps.forEach((step) => {
      // If the current step is an idle step and is found in idleReferences, it means that this step is waiting for a response from the previous step,
      // and we need to add a reference to the previous step
      if (idleReferences.some((ref) => ref.idleStepId === step.id)) {
        const ref = idleReferences.find(
          ({ idleStepId }) => idleStepId === step.id
        );
        invariant(ref);
        invariant(step.type === "idle");
        step.configs = {
          reference: ref.referenceStepId,
          ...step.configs,
        };
      }
    });
  });
}

/**
 * Create steps that are ready to be attached to a trigger with `dispatch(addSteps([steps]))`
 *
 * - Create steps based on template.steps
 * - Create placeholder steps if there is any reference in the steps
 * - Generate unique step ids and update references
 * - Auto-fill variable refs such as `$channelId`
 *
 * @param template
 * @param trigger
 * @param parentStepId
 * @returns new steps that are ready to be attached to a trigger
 */
export function createStepsFromTemplate(
  template: StepTemplate,
  trigger?: TriggerForEditor,
  parentStepId?: string
) {
  const explodedSteps = template.steps();
  const placeholderSteps = createPlaceholderSteps(explodedSteps);
  const stepsWithGeneratedId = generateStepIds([
    ...explodedSteps,
    ...placeholderSteps,
  ]);
  const steps = configureIdleStepAfterSendMessage(stepsWithGeneratedId);
  const finalSteps = trigger
    ? autoFillVariables(steps, trigger, parentStepId)
    : steps;
  return finalSteps;
}

/**
 * Keep `outputSteps` in sync with changes in `updatedStep`. This may modify other steps in `outputSteps`, for example, create/delete corresponding idle steps for multi-select when an option is added or removed.
 *
 * @param originalStep the step before the update
 * @param updatedStep the step after the update
 * @param outputSteps the output array of steps to be mutated
 */
export function syncWithTemplate(
  originalStep: TriggerStepCreate,
  updatedStep: TriggerStepCreate,
  outputSteps: TriggerStepCreate[]
) {
  // TODO what if there are more than one matching template?
  const template = STEP_TEMPLATES.find((t) => {
    const firstStep = t.steps()[0];
    return typeof firstStep === "object" && firstStep.type === updatedStep.type;
  });
  if (template) {
    switch (updatedStep.type) {
      case "send_multiple_select":
        syncMultiSelectWithTemplate(
          originalStep,
          updatedStep,
          template,
          outputSteps
        );
        break;
      case "send_survey":
        syncSurveyWithTemplate(
          originalStep,
          updatedStep,
          template,
          outputSteps
        );
        break;
      default:
        break;
    }
  }
}

/**
 * Replace step ids "on edges", i.e. currently targetStepId on transitions and `elseStepId` on `if`. If newId is null, delete the transition.
 *
 * @param idMap `{ "oldId": "newId"}
 * @param outputSteps an array of steps to be mutated
 */
function replaceStepIdsOnEdges(
  idMap: { [oldId: string]: string | null },
  outputSteps: TriggerStepCreate[]
) {
  const replaceStepId = (stepId: string) =>
    typeof idMap[stepId] === "undefined" ? stepId : idMap[stepId];
  for (let i = 0; i < outputSteps.length; i++) {
    let newStep = { ...outputSteps[i] };
    if (newStep.if) {
      const newId = replaceStepId(newStep.if.elseStepId);
      if (newId) newStep.if.elseStepId = newId;
    }
    const newTransitions = newStep.transitions
      .map((t) => {
        const newTargetId = replaceStepId(t.targetStepId);
        // This is a hack otherwise we get cannot-perform-get-on-a-proxy-that-has-been-revoked error,
        // It seems like a issue related to mutations, we need to investigate further
        return newTargetId
          ? { ...JSON.parse(JSON.stringify(t)), targetStepId: newTargetId }
          : null;
      })
      .filter((t) => t) as TriggerTransitionCreate[];
    newStep.transitions = newTransitions;
    outputSteps[i] = newStep;
  }
}

type Delta = {
  type: "append" | "delete";
  /**
   * which item do we delete or append after?
   */
  anchor: string;
  /**
   * E.g. what item to append? undefined if type = "delete"
   */
  data?: string;
  /**
   * The resulting new strings
   */
  newStrings: string[];
};

// export for unit test
/**
 *
 * Compute a list of changes needed to convert `before` into `after` using a set of primitive operations (currently "append" and "delete"). We use BFS to find the smallest number of changes needed.
 *
 * @param before
 * @param after
 * @returns a list of deltas
 */
export function computeDeltas(before: string[], after: string[]) {
  if (_.isEqual(before, after)) {
    return [];
  }

  // find the smallest diffs via BFS
  const getDeltas = (strings: string[]) => {
    const appends = after.map((s) => ({
      type: "append",
      anchor: strings[strings.length - 1],
      data: s,
      newStrings: strings.concat([s]),
    }));
    const deletions = before.map((id, index) => ({
      type: "delete",
      anchor: id,
      data: undefined,
      newStrings: before.slice(0, index).concat(before.slice(index + 1)),
    }));
    return [...appends, ...deletions] as Delta[];
  };
  const key = (strings: string[]) => JSON.stringify(strings);

  const queue: [string[], Delta[]][] = [[before, []]];
  const visited = new Set([key(before)]);
  while (queue.length > 0) {
    const [current, currentDeltas] = queue.shift()!;
    const deltas = getDeltas(current);
    for (const delta of deltas) {
      const { newStrings } = delta;
      const newDeltas = [...currentDeltas, delta];
      if (_.isEqual(newStrings, after)) {
        return newDeltas;
      }
      if (!visited.has(key(newStrings))) {
        visited.add(key(newStrings));
        queue.push([newStrings, newDeltas]);
      }
    }
  }
  return [];
}

/**
 * Keep `outputSteps` in sync with changes in `updatedStep`. This may modify other steps in `outputSteps`, for example, create/delete corresponding idle steps for multi-select when an option is added or removed.
 *
 * @param originalStep
 * @param updatedMultiSelectStep
 * @param template
 * @param outputSteps
 */
function syncMultiSelectWithTemplate(
  originalStep: TriggerStepCreate,
  updatedMultiSelectStep: TriggerStepCreate,
  template: StepTemplate,
  outputSteps: TriggerStepCreate[]
) {
  invariant(updatedMultiSelectStep.type === "send_multiple_select");
  invariant(originalStep?.type === "send_multiple_select");

  /**
   * Generate steps from the template based on the given delta (which has to be a `append`). We'll later insert these generated steps into the outputSteps array and attach them to the tree.
   *
   * @param template
   * @param delta
   * @returns a list of generated steps that correspond to the given delta
   */
  function generateStepsFromDelta(template: StepTemplate, delta: Delta) {
    invariant(updatedMultiSelectStep.type === "send_multiple_select");
    invariant(delta.type === "append" && delta.data);
    const updatedTemplate = {
      ...template,
      steps: () => {
        const steps = template.steps();
        const firstStep = steps[0];
        invariant(
          typeof firstStep === "object" &&
            firstStep.type === "send_multiple_select"
        );
        firstStep.configs.options =
          updatedMultiSelectStep.configs.options.filter((o) =>
            delta.newStrings.includes(o.id)
          );
        return template.steps(firstStep);
      },
    };

    const generatedSteps = createStepsFromTemplate(updatedTemplate);
    const [generatedMultiSelectStep, ...generatedIfElseSteps] = generatedSteps;
    // It's necessary to replace the generated multi-select step id. Otherwise the trigger will be broken.
    const newSteps = replaceStepIds(
      { [generatedMultiSelectStep.id]: updatedMultiSelectStep.id },
      generatedIfElseSteps,
      false
    );
    const index = newSteps.findIndex((s) =>
      isMatchingIfConditionStep(s, delta.data!)
    );
    return newSteps.slice(index, index + 2);
  }

  function isMatchingIfConditionStep(
    step: TriggerStepCreate,
    buttonId: string
  ) {
    const regex = new RegExp(`"array_contains":\\s*"${buttonId}"`);
    return step.if?.condition && JSON.stringify(step.if.condition).match(regex);
  }

  function findIfConditionStep(steps: TriggerStepCreate[], buttonId: string) {
    return steps.find((step) => isMatchingIfConditionStep(step, buttonId));
  }

  /**
   *  Main logic of syncMultiSelectWithTemplate starts here.
   * */
  const buttonIdsBefore = originalStep.configs.options.map((o) => o.id);
  const buttonIdsAfter = updatedMultiSelectStep.configs.options.map(
    (o) => o.id
  );
  const deltas = computeDeltas(buttonIdsBefore, buttonIdsAfter);
  for (const delta of deltas) {
    const anchorStep = findIfConditionStep(outputSteps, delta.anchor);
    switch (delta.type) {
      case "append": {
        // Insert generated steps after `delta.anchor`
        const generatedSteps = generateStepsFromDelta(template, delta);
        const lastGeneratedStep = generatedSteps[generatedSteps.length - 1];
        outputSteps.push(...generatedSteps);
        let downstreamId = null;
        if (anchorStep?.if) {
          downstreamId = anchorStep.if.elseStepId;
        } else if (updatedMultiSelectStep.transitions.length > 0) {
          downstreamId = updatedMultiSelectStep.transitions[0].targetStepId;
        }
        // insert generated steps after the anchor
        if (downstreamId) {
          const lastStepTargetId =
            lastGeneratedStep.transitions[0].targetStepId;
          replaceStepIdsOnEdges(
            {
              [downstreamId]: generatedSteps[0].id,
              [lastStepTargetId]: downstreamId,
            },
            outputSteps
          );
        } else {
          // if the multi-select points to nothing, we append the generated step
          updatedMultiSelectStep.transitions.push({
            targetStepId: generatedSteps[0].id,
          });
        }
        break;
      }
      case "delete": {
        const stepToDelete = anchorStep;
        invariant(stepToDelete && stepToDelete.if);
        // Remove the transitions targeting the elseStepId of stepToDelete. This effectively detaches the steps on the "yes" branch of stepToDelete.
        replaceStepIdsOnEdges(
          {
            [stepToDelete.if.elseStepId]: null,
          },
          outputSteps
        );
        // Just need to replace the references. `triggerEditorReducer.cleanupIdleSteps` will remove any dangling idle steps resulted from this.
        replaceStepIdsOnEdges(
          {
            [stepToDelete.id]: stepToDelete.if.elseStepId,
          },
          outputSteps
        );
        break;
      }
      default:
        break;
    }
  }
}

/**
 * Keep `outputSteps` in sync with changes in `updatedStep`
 *
 * @param originalStep
 * @param updatedSendSurveyStep
 * @param template
 * @param outputSteps
 */
function syncSurveyWithTemplate(
  originalStep: TriggerStepCreate,
  updatedSendSurveyStep: TriggerStepCreate,
  template: StepTemplate,
  outputSteps: TriggerStepCreate[]
) {
  invariant(updatedSendSurveyStep.type === "send_survey");
  invariant(originalStep?.type === "send_survey");

  if (
    updatedSendSurveyStep.configs.type !== "buttons" ||
    originalStep.configs.type !== "buttons"
  ) {
    // only support buttons for now
    return;
  }
  invariant(originalStep.configs.type === "buttons");
  invariant(updatedSendSurveyStep.configs.type === "buttons");

  /**
   * Generate steps from the template based on the given delta (which has to be a `append`). We'll later insert these generated steps into the outputSteps array and attach them to the tree.
   *
   * @param template
   * @param delta
   * @returns a list of generated steps that correspond to the given delta
   */
  function generateStepsFromDelta(template: StepTemplate, delta: Delta) {
    invariant(updatedSendSurveyStep.type === "send_survey");
    invariant(delta.type === "append" && delta.data);
    const updatedTemplate = {
      ...template,
      steps: () => {
        const steps = template.steps();
        const firstStep = steps[0];
        invariant(
          typeof firstStep === "object" && firstStep.type === "send_survey"
        );
        invariant(updatedSendSurveyStep.configs.type === "buttons");
        firstStep.configs.options =
          updatedSendSurveyStep.configs.options.filter((o) =>
            delta.newStrings.includes(o.id)
          );
        return template.steps(firstStep);
      },
    };

    const generatedSteps = createStepsFromTemplate(updatedTemplate);
    return generatedSteps;
  }

  function findTransitionIndexByButtonId(
    step: TriggerStepCreate,
    buttonId: string
  ) {
    invariant(step.type === "send_survey");
    const idx = step.transitions.findIndex((t) => {
      return (
        t.when?.userResponse.type === "button" &&
        t.when.userResponse.buttonId === buttonId
      );
    });
    return idx;
  }

  /**
   *  Main logic of syncSurveyWithTemplate starts here.
   * */
  const buttonIdsBefore = originalStep.configs.options.map((o) => o.id);
  const buttonIdsAfter = updatedSendSurveyStep.configs.options.map((o) => o.id);
  const deltas = computeDeltas(buttonIdsBefore, buttonIdsAfter);
  for (const delta of deltas) {
    const anchorTransitionIndex =
      typeof delta.anchor === "string"
        ? findTransitionIndexByButtonId(updatedSendSurveyStep, delta.anchor)
        : 0; // if delta.anchor=undefined, buttonIdsBefore=[]
    invariant(
      anchorTransitionIndex >= 0,
      `Failed to find the transition with button id ${delta.anchor} in ${updatedSendSurveyStep.id}`
    );
    switch (delta.type) {
      case "append": {
        // 1. create placeholder
        // We could also get the placeholder from generated steps, but creating it here is actually simpler
        const placeholder = createPlaceholderStep(
          `-${delta.data}-${generateStepId()}`
        );
        outputSteps.push(placeholder);
        // 2. Find the transition from generated step, insert it into updatedSendSurveyStep
        invariant(delta.data);
        const [generatedSendSurveyStep] = generateStepsFromDelta(
          template,
          delta
        );
        const transitionToInsert =
          generatedSendSurveyStep.transitions[
            findTransitionIndexByButtonId(generatedSendSurveyStep, delta.data)
          ];

        const transitionUpdated = produce(
          transitionToInsert,
          (draftTransition) => {
            const buttonUserResponse = (
              draftTransition as TriggerTransitionCreate
            ).when?.userResponse as WhenButtonUserResponse;
            if (buttonUserResponse.messageId) {
              buttonUserResponse.messageId = `{{${originalStep.id}.messageId}}`;
            }
          }
        );

        const transitions = updatedSendSurveyStep.transitions;
        updatedSendSurveyStep.transitions = [
          ...transitions.slice(0, anchorTransitionIndex + 1),
          {
            ...transitionUpdated,
            targetStepId: placeholder.id,
          } as TriggerTransitionCreate,
          ...transitions.slice(anchorTransitionIndex + 1),
        ];
        break;
      }
      case "delete": {
        const transitions = updatedSendSurveyStep.transitions;
        // Remove the transition at anchorTransitionIndex
        updatedSendSurveyStep.transitions = [
          ...transitions.slice(0, anchorTransitionIndex),
          ...transitions.slice(anchorTransitionIndex + 1),
        ];
        break;
      }
      default:
        break;
    }
  }
}
